import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Clock, Users, Copy, ArrowLeft, Heart, Calendar, TrendingUp, Award, Edit2, Camera, X, UserPlus, UserCheck, Bell, Star, ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { motion } from "framer-motion";
import ChefAvatar from "@/components/ChefAvatar";
import { GustoLogo, GustoIcon } from "@/components/GustoLogo";

const ChefPage = () => {
  const { id } = useParams();
  const { user, token } = useAuth();
  const [chef, setChef] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditBio, setShowEditBio] = useState(false);
  const [bio, setBio] = useState("");
  const [savingBio, setSavingBio] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [followLoading, setFollowLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("recipes");

  const isOwnProfile = user?.id === id;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [chefRes, recipesRes, galleryRes] = await Promise.all([
          axios.get(`${API}/chefs/${id}`),
          axios.get(`${API}/chefs/${id}/recipes`),
          axios.get(`${API}/chefs/${id}/gallery`)
        ]);
        setChef(chefRes.data);
        setRecipes(recipesRes.data);
        setGallery(galleryRes.data);
        setBio(chefRes.data.bio || "");
        setFollowersCount(chefRes.data.followers_count || 0);
        
        // Check if following
        if (token && user?.id !== id) {
          try {
            const followRes = await axios.get(`${API}/chefs/${id}/follow`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            setIsFollowing(followRes.data.following);
          } catch (e) {
            // Ignore
          }
        }
      } catch (error) {
        console.error("Error fetching chef:", error);
        toast.error("Chef introuvable");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, token, user?.id]);

  const handleFollow = async () => {
    if (!token) {
      toast.error("Connectez-vous pour suivre ce chef");
      return;
    }
    
    setFollowLoading(true);
    try {
      const response = await axios.post(
        `${API}/chefs/${id}/follow`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsFollowing(response.data.following);
      setFollowersCount(prev => response.data.following ? prev + 1 : prev - 1);
      
      if (response.data.following) {
        toast.success(`🔔 Vous suivez maintenant ${chef.pseudo} !`);
      }
    } catch (error) {
      toast.error("Erreur");
    } finally {
      setFollowLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  const handleSaveBio = async () => {
    setSavingBio(true);
    try {
      await axios.put(
        `${API}/auth/profile`,
        { bio },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setChef(prev => ({ ...prev, bio }));
      setShowEditBio(false);
      toast.success("Bio mise à jour !");
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSavingBio(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!chef) {
    return (
      <div className="px-4 py-8 text-center">
        <GustoLogo size="lg" />
        <p className="text-muted-foreground">Chef introuvable</p>
        <Link to="/discover">
          <Button className="mt-4 rounded-full">Retour à la découverte</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="pb-8" data-testid="chef-page">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-primary/20 via-accent/10 to-secondary/20 px-4 pt-6 pb-24">
        {/* Back button */}
        <Link 
          to="/discover" 
          className="inline-flex items-center gap-2 text-charcoal/70 hover:text-charcoal mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Découvrir
        </Link>

        {/* Profile Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col items-center text-center"
        >
          {/* Avatar with Toque */}
          <ChefAvatar
            src={chef.avatar}
            name={chef.pseudo}
            size="2xl"
            showToque={true}
            className="ring-4 ring-white rounded-full"
          />

          {/* Name */}
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-charcoal mt-4">
            Chef(fe) {chef.pseudo}
          </h1>

          {/* Followers count */}
          <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
            <Users className="w-4 h-4" />
            {followersCount} abonné{followersCount > 1 ? 's' : ''}
          </p>

          {/* Member since */}
          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            Membre depuis {formatDate(chef.created_at)}
          </p>

          {/* Follow Button */}
          {!isOwnProfile && (
            <Button
              onClick={handleFollow}
              disabled={followLoading}
              className={`mt-4 rounded-full px-6 gap-2 ${
                isFollowing 
                  ? 'bg-primary/10 text-primary hover:bg-primary/20 border border-primary/30' 
                  : 'bg-primary hover:bg-primary/90 text-white'
              }`}
            >
              {isFollowing ? (
                <>
                  <UserCheck className="w-4 h-4" />
                  Abonné
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4" />
                  S'abonner
                </>
              )}
            </Button>
          )}

          {/* Badge */}
          <Badge className="mt-3 bg-primary/10 text-primary border-primary/20">
            <GustoIcon className="w-4 h-4" />
            Chef Impro Certifié
          </Badge>
        </motion.div>
      </div>

      {/* Stats Cards */}
      <div className="px-4 -mt-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-3 gap-3 max-w-lg mx-auto"
        >
          <Card className="text-center p-4 shadow-lg">
            <p className="text-2xl md:text-3xl font-bold text-primary">{chef.recipes_count || 0}</p>
            <p className="text-xs text-muted-foreground">Recettes</p>
          </Card>
          <Card className="text-center p-4 shadow-lg">
            <div className="flex items-center justify-center gap-1">
              <Heart className="w-5 h-5 text-red-500 fill-red-500" />
              <p className="text-2xl md:text-3xl font-bold text-red-500">{chef.total_likes || 0}</p>
            </div>
            <p className="text-xs text-muted-foreground">J'adore</p>
          </Card>
          <Card className="text-center p-4 shadow-lg">
            <div className="flex items-center justify-center gap-1">
              <Copy className="w-5 h-5 text-accent" />
              <p className="text-2xl md:text-3xl font-bold text-accent">{chef.total_reproductions || 0}</p>
            </div>
            <p className="text-xs text-muted-foreground">Reproductions</p>
          </Card>
        </motion.div>
      </div>

      {/* Bio Section */}
      <section className="px-4 mt-8">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-lg font-semibold text-charcoal">
              À propos
            </h2>
            {isOwnProfile && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowEditBio(true)}
                className="text-primary"
              >
                <Edit2 className="w-4 h-4 mr-1" />
                Modifier
              </Button>
            )}
          </div>
          
          <Card className="p-4">
            {chef.bio ? (
              <p className="text-muted-foreground leading-relaxed">{chef.bio}</p>
            ) : (
              <p className="text-muted-foreground/60 italic">
                {isOwnProfile 
                  ? "Ajoutez une présentation pour que les autres chefs puissent en savoir plus sur vous..."
                  : "Ce chef n'a pas encore ajouté de présentation."}
              </p>
            )}
          </Card>
        </div>
      </section>

      {/* Photo Gallery */}
      {chef.gallery && chef.gallery.length > 0 && (
        <section className="px-4 mt-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-semibold text-charcoal flex items-center gap-2">
                <Camera className="w-5 h-5 text-primary" />
                Galerie photos
              </h2>
              <Badge variant="outline">{chef.gallery.length} photos</Badge>
            </div>
            
            <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
              {chef.gallery.map((item, index) => (
                <motion.div
                  key={item.recipe_id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  className="aspect-square rounded-xl overflow-hidden bg-muted cursor-pointer group relative"
                  onClick={() => setSelectedImage(item)}
                >
                  <img
                    src={item.image.startsWith('data:') ? item.image : `data:image/jpeg;base64,${item.image}`}
                    alt={item.recipe_name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-end">
                    <p className="text-white text-xs p-2 opacity-0 group-hover:opacity-100 transition-opacity truncate w-full">
                      {item.recipe_name}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Tabs: Recipes & Gallery */}
      <section className="px-4 mt-8" data-testid="chef-content-section">
        <div className="max-w-4xl mx-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full grid grid-cols-2 mb-6 h-12 rounded-2xl bg-muted/50">
              <TabsTrigger 
                value="recipes" 
                className="rounded-xl data-[state=active]:bg-primary data-[state=active]:text-white gap-2"
              >
                <GustoIcon className="w-4 h-4" />
                {isOwnProfile ? "Mes créations" : "Créations"} ({recipes.length})
              </TabsTrigger>
              <TabsTrigger 
                value="gallery"
                className="rounded-xl data-[state=active]:bg-primary data-[state=active]:text-white gap-2"
              >
                <ImageIcon className="w-4 h-4" />
                Galerie ({gallery.length})
              </TabsTrigger>
            </TabsList>

            {/* Recipes Tab */}
            <TabsContent value="recipes" className="mt-0">
              {recipes.length === 0 ? (
                <Card className="p-8 text-center">
                  <GustoLogo size="lg" />
                  <p className="text-muted-foreground">
                    {isOwnProfile 
                      ? "Vous n'avez pas encore publié de recette" 
                      : "Aucune recette publiée pour le moment"}
                  </p>
                  {isOwnProfile && (
                    <Link to="/record">
                      <Button className="mt-4 rounded-full bg-primary">
                        Créer ma première recette
                      </Button>
                    </Link>
                  )}
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {recipes.map((recipe, index) => (
                    <motion.div
                      key={recipe.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Link to={`/recipe/${recipe.id}`} data-testid={`chef-recipe-${recipe.id}`}>
                        <Card className="recipe-card overflow-hidden group cursor-pointer h-full">
                          <div className="aspect-video bg-gradient-to-br from-primary/20 to-accent/30 relative">
                            {recipe.final_image ? (
                              <img 
                                src={recipe.final_image.startsWith('data:') ? recipe.final_image : `data:image/jpeg;base64,${recipe.final_image}`}
                                alt={recipe.name}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <GustoLogo size="lg" />
                              </div>
                            )}
                            
                            {/* Stats badges */}
                            <div className="absolute top-3 right-3 flex gap-2">
                              {recipe.likes_count > 0 && (
                                <Badge className="bg-white/90 text-red-500">
                                  <Heart className="w-3 h-3 mr-1 fill-red-500" />
                                  {recipe.likes_count}
                                </Badge>
                              )}
                              {recipe.reproductions_count > 0 && (
                                <Badge className="bg-white/90 text-charcoal">
                                  <Copy className="w-3 h-3 mr-1" />
                                  {recipe.reproductions_count}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <CardContent className="p-4">
                            <h3 className="font-heading font-semibold text-lg text-charcoal group-hover:text-primary transition-colors line-clamp-1">
                              {recipe.name}
                            </h3>
                            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {(recipe.prep_time || 0) + (recipe.cook_time || 0)} min
                              </span>
                              <span className="flex items-center gap-1">
                                <Users className="w-4 h-4" />
                                {recipe.servings} parts
                              </span>
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Gallery Tab */}
            <TabsContent value="gallery" className="mt-0">
              {gallery.length === 0 ? (
                <Card className="p-8 text-center">
                  <ImageIcon className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground">
                    {isOwnProfile 
                      ? "Pas encore de photos de réalisations" 
                      : "Aucune réalisation partagée pour le moment"}
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Les photos apparaissent ici quand quelqu'un reproduit une recette
                  </p>
                </Card>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {gallery.map((item, index) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.05 }}
                      className="cursor-pointer"
                      onClick={() => setSelectedImage(item)}
                    >
                      <div className="aspect-square rounded-xl overflow-hidden bg-muted relative group">
                        <img 
                          src={item.completion_photo?.startsWith('data:') ? item.completion_photo : `data:image/jpeg;base64,${item.completion_photo}`}
                          alt={`Réalisation de ${item.user_pseudo}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3">
                          <p className="text-white text-sm font-medium line-clamp-1">{item.recipe_name}</p>
                          <div className="flex items-center gap-1 mt-1">
                            <Star className="w-3 h-3 text-accent fill-accent" />
                            <span className="text-white/80 text-xs">{item.rating}/5 par {item.user_pseudo}</span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Edit Bio Dialog */}
      <Dialog open={showEditBio} onOpenChange={setShowEditBio}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Ma présentation</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="Parlez de vous, votre passion pour la cuisine, vos spécialités..."
              className="min-h-[150px] rounded-xl resize-none"
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground mt-2 text-right">
              {bio.length}/500 caractères
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditBio(false)} className="rounded-full">
              Annuler
            </Button>
            <Button onClick={handleSaveBio} disabled={savingBio} className="rounded-full bg-primary">
              {savingBio ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="sm:max-w-2xl p-0 overflow-hidden">
          {selectedImage && (
            <>
              <div className="relative">
                <img
                  src={selectedImage.completion_photo?.startsWith('data:') ? selectedImage.completion_photo : `data:image/jpeg;base64,${selectedImage.completion_photo}`}
                  alt={selectedImage.recipe_name}
                  className="w-full h-auto max-h-[60vh] object-contain bg-black"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white rounded-full"
                  onClick={() => setSelectedImage(null)}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="p-4 bg-white space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading font-semibold text-charcoal">{selectedImage.recipe_name}</h3>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className={`w-4 h-4 ${i < selectedImage.rating ? 'text-accent fill-accent' : 'text-muted'}`} />
                    ))}
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <ChefAvatar
                    src={selectedImage.user_avatar}
                    name={selectedImage.user_pseudo}
                    size="sm"
                    showToque={false}
                  />
                  <div>
                    <p className="text-sm font-medium">{selectedImage.user_pseudo}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(selectedImage.created_at).toLocaleDateString('fr-FR', { 
                        day: 'numeric', 
                        month: 'long', 
                        year: 'numeric' 
                      })}
                    </p>
                  </div>
                </div>
                
                {selectedImage.comment && (
                  <p className="text-sm text-muted-foreground italic">"{selectedImage.comment}"</p>
                )}
                
                <Link 
                  to={`/recipe/${selectedImage.recipe_id}`}
                  className="inline-block text-sm text-primary hover:underline"
                >
                  Voir la recette →
                </Link>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChefPage;
